import React, { useState, useEffect } from 'react';

export const InventoryDeployment = ({ token }) => {
    const [organizations, setOrganizations] = useState([]);
    const [selectedOrgId, setSelectedOrgId] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const buildHierarchy = (orgs, parentId = null, depth = 0) => {
        let result = [];
        const children = orgs.filter(o => o.ParentOrganizationID === parentId);
        for (const child of children) {
            result.push({ ...child, depth });
            result = result.concat(buildHierarchy(orgs, child.OrganizationID, depth + 1));
        }
        return result;
    };

    useEffect(() => {
        const fetchOrgs = async () => {
            try {
                const res = await fetch(`/api/inventory/organizations?t=${Date.now()}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    const hierarchicalData = buildHierarchy(data);
                    setOrganizations(hierarchicalData);
                } else {
                    setError('Failed to fetch organizations.');
                }
            } catch (err) {
                console.error(err);
                setError('Error connecting to server.');
            }
        };
        fetchOrgs();
    }, [token]);

    const handleDownload = () => {
        if (!selectedOrgId) {
            setError('Please select an organization first.');
            return;
        }
        setError('');
        setIsLoading(true);

        // We trigger download by creating a temporary link
        // Passing token in URL isn't standard, but since it's a GET request for a download, 
        // normally we'd use a temporary download ticket or cookie. 
        // For simplicity and since we have an API endpoint that checks the Bearer token,
        // we can fetch the blob and then trigger download.
        
        fetch(`/api/scripts/download/inventory?orgId=${selectedOrgId}`, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${token}` }
        })
        .then(response => {
            if (!response.ok) throw new Error('Download failed');
            return response.blob();
        })
        .then(blob => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = 'GatherInventory.cmd';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            setIsLoading(false);
        })
        .catch(err => {
            console.error(err);
            setError('Failed to download the deployment script.');
            setIsLoading(false);
        });
    };

    return (
        <div className="flex flex-col h-full space-y-6">
            <div className="bg-slate-800 rounded-lg shadow border border-slate-700 p-6 flex-none max-w-2xl">
                <div className="mb-4">
                    <h3 className="text-xl font-semibold text-slate-100 flex items-center gap-2">
                        <span className="material-symbols-outlined text-blue-400">deployed_code</span>
                        Inventory Script Deployment
                    </h3>
                    <p className="text-sm text-slate-400 mt-1">
                        Download a pre-configured PowerShell agent to gather system telemetry. The downloaded script will automatically report data back to the server and file it under the selected organization.
                    </p>
                </div>

                {error && (
                    <div className="mb-4 p-3 bg-red-900/30 border border-red-500/50 rounded-md flex items-start text-red-200 text-sm">
                        <span className="material-symbols-outlined mr-2 text-[18px]">error</span>
                        <span>{error}</span>
                    </div>
                )}

                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-300 mb-1">Target Organization</label>
                        <select 
                            value={selectedOrgId}
                            onChange={(e) => setSelectedOrgId(e.target.value)}
                            className="w-full bg-slate-900 border border-slate-600 rounded-md px-3 py-2 text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
                        >
                            <option value="">-- Select Organization --</option>
                            {organizations.map(org => (
                                <option key={org.OrganizationID} value={org.OrganizationID}>
                                    {'\u00A0\u00A0\u00A0\u00A0'.repeat(org.depth)}{org.OrganizationName} {org.OrganizationType ? `(${org.OrganizationType})` : ''}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="pt-2">
                        <button 
                            onClick={handleDownload}
                            disabled={isLoading || !selectedOrgId}
                            className="flex items-center justify-center w-full px-4 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-md font-medium transition-colors"
                        >
                            <span className="material-symbols-outlined mr-2">download</span>
                            {isLoading ? 'Generating Payload...' : 'Download Agent Script'}
                        </button>
                    </div>
                </div>
            </div>
            
            <div className="bg-slate-800 rounded-lg shadow border border-slate-700 p-6 flex-1 max-w-2xl">
                <h4 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-4 border-b border-slate-700 pb-2">Deployment Instructions</h4>
                <ol className="list-decimal list-inside space-y-3 text-sm text-slate-400">
                    <li>Select the target Organization from the dropdown above.</li>
                    <li>Click <strong className="text-slate-300">Download Agent Script</strong> to generate a custom <code>GatherInventory.ps1</code> script.</li>
                    <li>Transfer the script to the target Windows machine.</li>
                    <li>Right-click the script and select <strong className="text-slate-300">Run with PowerShell</strong>.</li>
                    <li>The agent will automatically collect telemetry (Hardware, Software, Drives, Network) and securely report it to the central server.</li>
                </ol>
            </div>
        </div>
    );
};
